import { useCallback } from 'react'

import { useApolloClient } from '@apollo/client/react'

import { useSplitEnabled } from '@buffer-mono/features'

import callRpc from '~publish/legacy/utils/call-rpc'

import { GetPostForComposer } from './GetPostForComposer'
import { postToRpcUpdate } from './postToRpcUpdate'

/**
 * Organization-scoped rollout of loading composer posts from the GraphQL
 * `post` query instead of the `getPost` RPC.
 */
export const COMPOSER_GET_POST_GRAPHQL_SPLIT = 'composer-get-post-graphql'

export type FetchPostForComposer = (postId: string) => Promise<unknown>

/**
 * Returns a loader for the post the composer opens to edit or duplicate. The
 * result is untrusted (`unknown`), as the RPC's always was: callers narrow it
 * with `isRpcUpdate`.
 */
export function useFetchPostForComposer(): FetchPostForComposer {
  const client = useApolloClient()
  const { isEnabled, isReady } = useSplitEnabled(
    COMPOSER_GET_POST_GRAPHQL_SPLIT,
  )
  const isGraphqlEnabled = isReady && isEnabled

  return useCallback(
    async (postId: string): Promise<unknown> => {
      if (!isGraphqlEnabled) {
        return callRpc('getPost', { updateId: postId })
      }

      const { data } = await client.query({
        query: GetPostForComposer,
        variables: { id: postId },
        // The composer edits what is stored now, never a cached copy.
        fetchPolicy: 'network-only',
      })
      if (!data) throw new Error(`Post ${postId} could not be loaded`)

      return postToRpcUpdate(data.post)
    },
    [client, isGraphqlEnabled],
  )
}
